<template>
  <v-card class="full-height card-container">
    <!-- App Bar -->
    <div class="calendar-app-bar" :class="{ 'drawer-open': drawer }">
      <v-btn icon @click="drawer = !drawer" class="mr-2" depressed>
        <v-icon>mdi-menu</v-icon>
      </v-btn>

      <v-btn
        icon
        @click="previousPeriod"
        class="ma-1"
        color="primary"
        small
        depressed
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>

      <v-btn
        color="primary"
        outlined
        @click="setToday"
        class="mx-2"
        small
        depressed
      >
        Hoje
      </v-btn>

      <v-btn
        icon
        @click="nextPeriod"
        class="ma-1"
        color="primary"
        small
        depressed
      >
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
      <v-spacer></v-spacer>

      <v-btn-toggle
        v-model="type"
        color="primary"
        class="mr-4"
        style="border-radius: 10px"
      >
        <v-btn height="40" value="month" small depressed> Mês </v-btn>
        <v-btn height="40" value="week" small depressed> Semana </v-btn>
        <v-btn height="40" value="day" small depressed> Dia </v-btn>
      </v-btn-toggle>

      <v-btn
        class="primary-button ma-1"
        @click="showPatternRequestDialog = true"
        depressed
      >
        Solicitação de Padrão
      </v-btn>

      <v-btn
        class="primary-button ma-1"
        @click="showEventDialog = true"
        depressed
      >
        <v-icon left>mdi-plus</v-icon>
        Novo agendamento
      </v-btn>
    </div>

    <!-- Sidebar Navigation -->
    <v-navigation-drawer
      v-model="drawer"
      absolute
      width="300"
      class="calendar-sidebar"
    >
      <!-- Create Button -->
      <div class="pa-4 py-8 d-flex justify-center align-center">
        <div class="main-toolbar d-flex align-center">
          <div class="icon-back" style="width: 45px; height: 45px">
            <v-icon color="white" size="20">mdi-calendar-clock</v-icon>
          </div>

          <span class="main-toolbar-title mt-2" style="font-size: 16px">
            Central de Equipamentos
          </span>
        </div>
      </div>

      <v-divider></v-divider>

      <!-- Mini Calendar -->
      <div class="pa-4">
        <div class="mini-calendar-container">
          <!-- Calendar Header -->
          <div
            class="mini-calendar-header d-flex align-center justify-space-between mb-2"
          >
            <v-btn icon small @click="previousMonth" class="calendar-nav-btn">
              <v-icon small>mdi-chevron-left</v-icon>
            </v-btn>

            <date-picker
              v-model="miniCalendarPickerDate"
              mode="date"
              locale="pt-BR"
              :popover="{ visibility: 'click', positionFixed: true }"
              @input="navigateToSelectedDate"
            >
              <template v-slot="{ inputValue, inputEvents }">
                <div
                  class="mini-calendar-title text-subtitle-2 font-weight-medium"
                  v-on="inputEvents"
                >
                  {{ currentMonthYear }}
                </div>
              </template>
            </date-picker>

            <v-btn icon small @click="nextMonth" class="calendar-nav-btn">
              <v-icon small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>

          <!-- Calendar Grid -->
          <div class="mini-calendar-grid">
            <!-- Weekday Headers -->
            <div class="mini-calendar-weekdays">
              <div
                v-for="(day, index) in weekDays"
                :key="index"
                class="mini-calendar-weekday"
              >
                {{ day }}
              </div>
            </div>

            <!-- Calendar Days -->
            <div class="mini-calendar-days">
              <div
                v-for="(day, index) in calendarDays"
                :key="index"
                class="mini-calendar-day"
                :class="{
                  'other-month': day.otherMonth,
                  today: day.isToday,
                  selected: day.isSelected,
                  'has-events': day.hasEvents,
                }"
                @click="selectDate(day)"
              >
                <span class="day-number">{{ day.day }}</span>
                <div v-if="day.hasEvents" class="event-indicators">
                  <div
                    v-for="(event, eventIndex) in day.events.slice(0, 2)"
                    :key="eventIndex"
                    class="event-dot"
                    :style="{ backgroundColor: event.color }"
                  >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <div
                          v-bind="attrs"
                          v-on="on"
                          class="event-dot-inner"
                        ></div>
                      </template>
                      <span>
                        <strong>{{
                          event.name || event.calibrationPatternName
                        }}</strong>
                        <br v-if="event.startHour && event.endHour" />
                        <span v-if="event.startHour && event.endHour">
                          {{ event.startHour }} - {{ event.endHour }}
                        </span>
                        <br v-if="event.destinationInstitutionName" />
                        <span v-if="event.destinationInstitutionName">
                          {{ event.destinationInstitutionName }}
                        </span>
                      </span>
                    </v-tooltip>
                  </div>
                  <div v-if="day.events.length > 2" class="more-events">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">
                          +{{ day.events.length - 2 }}
                        </span>
                      </template>
                      <span>
                        <div
                          v-for="(event, idx) in day.events.slice(2)"
                          :key="idx"
                        >
                          {{ event.name || event.calibrationPatternName }}
                        </div>
                      </span>
                    </v-tooltip>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <v-divider></v-divider>

      <!-- Padrões Section -->
      <div class="patterns-section">
        <div class="pa-4 pb-2">
          <div class="text-subtitle-1 font-weight-medium mb-3">
            Padrões
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small class="ml-2" v-bind="attrs" v-on="on"
                  >mdi-information-outline</v-icon
                >
              </template>
              <span
                >Arraste um padrão para o calendário para abrir o formulário de
                agendamento</span
              >
            </v-tooltip>
          </div>
        </div>
        <div class="d-flex align-center justify-center pt-0 pb-2">
          <!-- Campo de busca continua igual -->
          <v-text-field
            v-model="searchPattern"
            label="Pesquisar padrões"
            class="primary-input ml-4 mb-0 pa-0"
            outlined
            dense
            hide-details
            @keyup.enter="applyPatternSearchFilter"
            clearable
            @click:clear="clearPatternSearch"
            ><template v-slot:append>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-bind="attrs" v-on="on">
                    mdi-progress-question
                  </v-icon>
                </template>
                <span>Buscar por nome, número de série ou patrimônio</span>
              </v-tooltip>
            </template></v-text-field
          >

          <!-- Menu popup ao clicar no botão -->
          <v-menu
            v-model="showFilter"
            :close-on-content-click="false"
            offset-y
            transition="scale-transition"
            max-width="700"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="primary-button ml-2"
                :color="isFilterActive ? 'secondary-button' : 'primary-button'"
                small
                style="height: 40px; min-width: 40px"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-filter</v-icon>
              </v-btn>
            </template>

            <v-card class="pa-4" style="max-width: 500px">
              <v-row dense>
                <v-col cols="12" sm="6">
                  <label class="label">{{ $t('Instituição') }}</label>
                  <v-autocomplete
                    hide-details="auto"
                    v-model="institutionId"
                    :items="institutions"
                    @change="getUnidades"
                    item-value="id"
                    item-text="name"
                    outlined
                    class="primary-input"
                    append-icon=""
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6">
                  <label class="label">{{ $t('Unidade') }}</label>
                  <v-autocomplete
                    hide-details="auto"
                    v-model="unidadeId"
                    :items="unidades"
                    item-value="id"
                    item-text="name"
                    :disabled="!institutionId"
                    :placeholder="
                      !institutionId ? $t('Selecione uma instituição') : ''
                    "
                    outlined
                    class="primary-input"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="6">
                  <label class="label">{{ $t('Modelo') }}</label>
                  <v-text-field
                    hide-details="auto"
                    v-model="patternModel"
                    outlined
                    class="primary-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <label class="label">{{ $t('Fabricante') }}</label>
                  <v-autocomplete
                    hide-details="auto"
                    v-model="patternManufacturer"
                    :items="manufacturers"
                    item-text="name"
                    item-value="name"
                    outlined
                    append-icon="mdi-chevron-down"
                    class="primary-input"
                    @keyup.enter="filterTable"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="12">
                  <label class="label">{{ $t('Alienado') }}</label>
                  <v-autocomplete
                    hide-details="auto"
                    v-model="patternAlienated"
                    :items="alienatedOptions"
                    item-text="text"
                    item-value="value"
                    :placeholder="$t('Não Alienados')"
                    outlined
                    class="primary-input"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" class="d-flex justify-end align-end">
                  <v-btn
                    class="mx-2 secondary-button"
                    color="#BBCF20"
                    @click="filterTable"
                    depressed
                  >
                    <span>{{ $t('Filtrar') }}</span>
                    <v-icon class="pl-2">mdi-magnify</v-icon>
                  </v-btn>

                  <v-btn
                    depressed
                    color="#20202066"
                    class="clear-button"
                    @click="reset"
                  >
                    <span>{{ $t('Limpar') }}</span>
                    <v-icon class="pl-2">mdi-cached</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-menu>
        </div>

        <div class="patterns-list-container">
          <v-list dense class="patterns-list">
            <v-list-item
              v-for="pattern in calibrationV2Patterns"
              :key="pattern.id"
              @click="togglePattern(pattern)"
              class="px-4 pattern-item"
              :class="{
                'pattern-selected': selectedPatterns.some(
                  (p) => p.id === pattern.id
                ),
              }"
              draggable="true"
              @dragstart="onPatternDragStart($event, pattern)"
              @dragend="onPatternDragEnd"
            >
              <v-tooltip
                top
                color="white"
                content-class="custom-tooltip"
                open-delay="200"
              >
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                    style="width: 100%; display: flex"
                  >
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2 font-weight-medium">
                        <v-icon
                          v-if="pattern.borrowedOnInstitutionId != null"
                          small
                          color="orange"
                          class="ml-2"
                        >
                          mdi-tray-arrow-up
                        </v-icon>
                        <v-icon v-else small color="green" class="ml-2">
                          mdi-check-circle
                        </v-icon>
                        {{ pattern.name }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        {{ pattern.description }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-icon small class="drag-handle">mdi-drag</v-icon>
                    </v-list-item-action>
                  </div>
                </template>
                <span>
                  <strong>{{ pattern.name }}</strong
                  ><br />
                  <span v-if="pattern.description">{{
                    pattern.description
                  }}</span>
                  <span v-if="pattern.model"
                    >Modelo: {{ pattern.model }}<br
                  /></span>
                  <span v-if="pattern.manufacturer"
                    >Fabricante: {{ pattern.manufacturer }}<br
                  /></span>
                  <span v-if="pattern.serialNumber"
                    >Nº Série: {{ pattern.serialNumber }}<br
                  /></span>
                  <span v-if="pattern.propertyCode"
                    >Patrimônio: {{ pattern.propertyCode }}<br
                  /></span>
                  <span v-if="pattern.borrowedOnInstitution?.name"
                    >Emprestado para: {{ pattern.borrowedOnInstitution?.name
                    }}<br
                  /></span>
                </span>
              </v-tooltip>
            </v-list-item>
          </v-list>
        </div>

        <v-btn
          class="secondary-button ma-2 mt-2"
          @click="showEventDialog = true"
          depressed
          style="margin-top: auto; flex-shrink: 0"
        >
          <v-icon left>mdi-plus</v-icon>
          Novo agendamento
        </v-btn>
      </div>
    </v-navigation-drawer>

    <!-- Main Content Area -->
    <div class="calendar-main-content" :class="{ 'drawer-open': drawer }">
      <!-- Title Bar -->
      <div class="calendar-title-bar text-center pa-4">
        <div class="text-h5 font-weight-medium">
          {{ title }}
        </div>
      </div>

      <!-- Main Calendar -->
      <div
        class="calendar-drop-zone"
        @dragover.prevent="onCalendarDragOver"
        @drop.prevent="onCalendarDrop"
        @dragleave="onCalendarDragLeave"
        :class="{
          'drag-over': dragOverDate && (draggedEvent || draggedPattern),
        }"
      >
        <!-- Zona navegação: mês anterior -->
        <div
          v-if="draggedEvent || draggedPattern"
          class="drag-nav-zone drag-nav-zone--left"
          :class="{ 'drag-nav-zone--active': dragNavZone === 'left' }"
          @dragenter.prevent="onNavZoneEnter('left')"
          @dragleave="onNavZoneLeave"
          @dragover.prevent
          @drop.prevent
        >
          <v-icon color="white" class="mb-1">mdi-chevron-left</v-icon>
          <span class="drag-nav-zone__label">Mês anterior</span>
        </div>
        <!-- Zona navegação: próximo mês -->
        <div
          v-if="draggedEvent || draggedPattern"
          class="drag-nav-zone drag-nav-zone--right"
          :class="{ 'drag-nav-zone--active': dragNavZone === 'right' }"
          @dragenter.prevent="onNavZoneEnter('right')"
          @dragleave="onNavZoneLeave"
          @dragover.prevent
          @drop.prevent
        >
          <v-icon color="white" class="mb-1">mdi-chevron-right</v-icon>
          <span class="drag-nav-zone__label">Próximo mês</span>
        </div>
        <v-calendar
          ref="calendar"
          v-model="focus"
          color="primary"
          :events="visibleEvents"
          :event-margin-bottom="3"
          :event-color="getEventColor"
          :event-ripple="false"
          :type="type"
          @click:event="showEvent"
          @click:more="viewDay"
          @click:date="clickDate"
          @change="updateRange"
          @mousedown:event="startDrag"
          @mousedown:time="startTime"
          @mousemove:time="mouseMove"
          @mouseup:time="endDrag"
          @mouseleave.native="cancelDrag"
          @dragover.native.prevent
          @drop.native.prevent
          class="calendar-main"
          locale="pt-br"
        >
          <template v-slot:day-label="{ outside, day, date }">
            <span
              v-if="!outside"
              class="v-btn v-btn--fab v-btn--has-bg v-btn--rounded theme--light"
              style="width: 32px; height: 32px; font-size: 12px"
              :data-date="date"
              >{{ day }}</span
            >
          </template>

          <template v-slot:event="{ event, timed, eventSummary }">
            <div
              class="v-event-draggable"
              :draggable="!!event.id"
              @dragstart.stop="onEventDragStart($event, event)"
              @dragend.stop="onEventDragEnd"
              @click.stop="openEventDetails(event)"
              :title="event.id ? 'Arraste para mover • Clique para editar' : ''"
              :style="{ cursor: event.id ? 'move' : 'pointer' }"
              :class="{
                'event-dragging': draggedEvent && draggedEvent.id === event.id,
              }"
            >
              <component :is="{ render: eventSummary }"></component>
            </div>
            <div
              v-if="timed"
              class="v-event-drag-bottom"
              @mousedown.stop="extendBottom(event)"
            ></div>
          </template>
        </v-calendar>
      </div>
    </div>

    <!-- Event Details Dialog -->
    <EventDetailsDialog
      v-model="selectedOpen"
      :event="selectedEvent"
      @edit-event="editEvent"
      @event-deleted="removeScheduling"
    />

    <!-- Create/Edit Event Dialog -->
    <EventFormDialog
      v-model="showEventDialog"
      :editing-event="editingEvent"
      :event-data="eventForm"
      :institution="institution"
      :scheduling-event-types="schedulingEventTypes"
      :suppliers="suppliers"
      @save-event="handleSaveEvent"
      @institution-selected="getUnidades"
      @event-saved="reloadScheduling"
    />

    <v-dialog v-model="showPatternRequestDialog" max-width="1000">
      <RequestModal @close="showPatternRequestDialog = false" />
    </v-dialog>
  </v-card>
</template>

<script>
import EventDetailsDialog from './components/EventDetailsDialog.vue'
import EventFormDialog from './components/EventFormDialog.vue'
import RequestModal from './../CalibrationV2/components/pattern/requestsModal.vue'
import ApiService from '@/services/ApiService.js'

export default {
  name: 'EquipmentCentral',
  components: {
    EventDetailsDialog,
    EventFormDialog,
    RequestModal,
  },
  data() {
    return {
      eventColors: [
        { name: 'Empréstimo', value: '#2196f3', type: 0 },
        { name: 'Translado', value: '#ff9800', type: 1 },
        { name: 'Calibração', value: '#5b5b5b', type: 2 },
      ],
      apiService: new ApiService(),
      institution: null,
      drawer: true,
      focus: '',
      type: 'month',
      miniCalendarValue: '',
      miniCalendarDate: new Date(),
      selectedMiniDate: new Date(),
      weekDays: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
      typeToLabel: {
        month: 'Mês',
        week: 'Semana',
        day: 'Dia',
      },
      start: null,
      end: null,
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      showEventDialog: false,
      editingEvent: false,
      eventForm: {
        name: '',
        startDateTime: '',
        endDateTime: '',
        calibrationPattern: null,
        destinationInstitution: null,
        destinationUnidade: null,
        schedulingEventType: 0, // Default Loan
        details: '',
        supplierId: null,
      },
      schedulingEventTypes: [
        { name: 'Loan', value: 0, label: 'Empréstimo' },
        { name: 'Translado', value: 1, label: 'Translado' },
        { name: 'Calibration', value: 2, label: 'Calibração' },
      ],
      selectedPatterns: [],
      calibrationV2Patterns: [],
      institutions: [],
      unidades: [],
      events: [],
      miniCalendarEvents: [],
      draggedPattern: null,
      dragOverDate: null,
      dragEvent: null,
      dragTime: null,
      createEvent: null,
      createStart: null,
      extendOriginal: null,
      draggedEvent: null,
      draggedEventDuration: 0,
      lastHighlightedDay: null,
      dragNavZone: null,
      searchPattern: '',
      showFilter: false,
      filter: {
        institutionId: null,
        unidadeId: null,
        patternModel: null,
        patternManufacturer: null,
        patternAlienated: false,
      },
      unidadeId: null,
      patternModel: null,
      patternManufacturer: null,
      patternAlienated: false,
      alienatedOptions: [
        { text: 'Todos', value: null },
        { text: 'Alienados', value: true },
        { text: 'Não alienados', value: false },
      ],
      manufacturers: [],
      institutionId: null,
      user: [],
      showPatternRequestDialog: false,
      loading: false,
      selectedPattern: null,
      allEvents: [],
      filteredEvents: [],
      suppliers: [],
      miniCalendarPickerDate: new Date(),
      activeSearchFilter: null,
    }
  },
  created() {
    this.institution = JSON.parse(localStorage.getItem('institution'))
    this.user = JSON.parse(localStorage.getItem('user'))
    this.getScheduling()
    this.getMiniCalendarScheduling()
    this.getManufacturers()
    this.getFilteredPatterns()
    this.getSupplier()
  },

  computed: {
    title() {
      try {
        if (!this.start || !this.end) return '-'

        const start = this.start
        const end = this.end

        const startMonth = this.monthFormatter(start)
        const endMonth = this.monthFormatter(end)

        const startYear = start.year
        const endYear = end.year

        const startDay = start.day
        const endDay = end.day

        const sameMonth = startMonth === endMonth && startYear === endYear

        if (this.type === 'month') {
          // mês sempre pelo início do range
          return `${startMonth} ${startYear}`
        }

        if (this.type === 'week' || this.type === '4day') {
          if (sameMonth) {
            return `${startDay}–${endDay} ${startMonth} ${startYear}`
          }

          return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`
        }

        if (this.type === 'day') {
          return `${startDay} ${startMonth} ${startYear}`
        }

        return '-'
      } catch (error) {
        console.error('Error in title computed:', error)
        return '-'
      }
    },
    visibleEvents() {
      const events = this.events.map((event) => ({
        ...event,
        start: event.start,
        end: event.end,
        timed: event.timed !== false,
      }))

      if (this.createEvent) {
        events.push(this.createEvent)
      }
      return events
    },
    currentMonthYear() {
      if (!this.miniCalendarDate || isNaN(this.miniCalendarDate.getTime())) {
        console.error(
          'Data inválida do miniCalendário em currentMonthYear:',
          this.miniCalendarDate
        )
        return new Date().toLocaleDateString('pt-BR', {
          month: 'long',
          year: 'numeric',
        })
      }
      return this.miniCalendarDate.toLocaleDateString('pt-BR', {
        month: 'long',
        year: 'numeric',
      })
    },
    calendarDays() {
      let year, month

      if (!this.miniCalendarDate || isNaN(this.miniCalendarDate.getTime())) {
        console.error(
          'Data inválida do miniCalendário em calendarDays:',
          this.miniCalendarDate
        )
        // Padrão para o mês atual
        const today = new Date()
        year = today.getFullYear()
        month = today.getMonth()
      } else {
        year = this.miniCalendarDate.getFullYear()
        month = this.miniCalendarDate.getMonth()
      }

      // First day of the month
      const firstDay = new Date(year, month, 1)
      const lastDay = new Date(year, month + 1, 0)

      // Start from Sunday of the week containing the first day
      const startDate = new Date(firstDay)
      startDate.setDate(startDate.getDate() - startDate.getDay())

      // End on Saturday of the week containing the last day
      const endDate = new Date(lastDay)
      endDate.setDate(endDate.getDate() + (6 - endDate.getDay()))

      const days = []
      const currentDate = new Date(startDate)
      const today = new Date()

      while (currentDate <= endDate) {
        const dayEvents = this.getEventsForDate(currentDate, 'mini')
        const isCurrentMonth = currentDate.getMonth() === month
        const isToday = this.isSameDate(currentDate, today)
        const isSelected = this.isSameDate(currentDate, this.selectedMiniDate)

        // Create a new date object to avoid reference issues
        const dayDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          currentDate.getDate()
        )

        days.push({
          day: currentDate.getDate(),
          date: dayDate,
          otherMonth: !isCurrentMonth,
          isToday,
          isSelected,
          hasEvents: dayEvents.length > 0,
          events: dayEvents,
        })

        currentDate.setDate(currentDate.getDate() + 1)
      }

      return days
    },
    isFilterActive() {
      return (
        this.filter.institutionId !== null ||
        this.filter.unidadeId !== null ||
        !!this.filter.patternModel ||
        !!this.filter.patternManufacturer
      )
    },
  },

  watch: {
    start: {
      handler() {
        this.$nextTick(() => {
          setTimeout(() => {
            this.hideOutsideDaysCSS()
          }, 100)
        })
      },
      deep: true,
    },
    type() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.hideOutsideDaysCSS()
        }, 100)
      })
    },
  },

  mounted() {
    const today = new Date()
    this.focus = this.getDateString(today)
    this.type = 'month'

    this.$refs.calendar.checkChange()

    if (!this.miniCalendarDate || isNaN(this.miniCalendarDate.getTime())) {
      this.miniCalendarDate = new Date()
    }
    if (!this.selectedMiniDate || isNaN(this.selectedMiniDate.getTime())) {
      this.selectedMiniDate = new Date()
    }

    this.$nextTick(() => {
      setTimeout(() => {
        this.setupCalendarDayHoverListeners()
        this.hideOutsideDaysCSS()
      }, 500)
    })
  },

  updated() {
    this.$nextTick(() => {
      setTimeout(() => {
        this.setupCalendarDayHoverListeners()
        this.hideOutsideDaysCSS()
      }, 300)
    })
  },

  methods: {
    togglePattern(pattern) {
      const idx = this.selectedPatterns.findIndex((p) => p.id === pattern.id)

      if (idx === -1) {
        this.selectedPatterns.push(pattern)
      } else {
        this.selectedPatterns.splice(idx, 1)
      }

      this.filterEventsByPattern()
    },

    getColorByEventType(type) {
      const found = this.eventColors.find((c) => c.type === type)
      return found ? found.value : '#2196f3'
    },
    async reloadScheduling() {
      // Se o calendário tem um range visível (start/end), recarrega com essas datas
      if (this.start && this.end) {
        const startDate = new Date(`${this.start.date}T00:00:00`)
        const endDate = new Date(`${this.end.date}T23:59:59`)

        if (this.type === 'month') {
          startDate.setDate(startDate.getDate() - 6)
          endDate.setDate(endDate.getDate() + 6)
        }

        await this.getScheduling(startDate, endDate)
      } else {
        // Caso contrário, recarrega o mês atual
        await this.getScheduling()
      }
      await this.getMiniCalendarScheduling()
    },

    async getSupplier() {
      await this.apiService
        .get(`supplier/list/${this.institution.id}`)
        .then((response) => {
          this.suppliers = response.content
        })
        .catch((error) => {
          console.error('Erro ao obter fornecedores', error)
          this.$toast.error('Erro ao obter fornecedores')
        })
    },

    async getScheduling(startDate = null, endDate = null) {
      const now = new Date()

      if (!startDate || !endDate) {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1)
        endDate = new Date(
          now.getFullYear(),
          now.getMonth() + 1,
          0,
          23,
          59,
          59,
          999
        )
      }

      const filter = {
        institutionId: this.institution.id,
        startDate: this.getDateString(startDate),
        endDate: this.getDateString(endDate),
      }

      await this.apiService
        .post(`scheduling/list-by-instituitionid`, filter)
        .then((response) => {
          const parsedEvents = response.content.map((event) => {
            // Monta o nome base
            let baseName = ''
            if (event.schedulingPatterns) {
              baseName = event.schedulingPatterns
                .map((x) => {
                  const propertyCode = x.pattern?.propertyCode || 'S/P'
                  const patternName = x.pattern?.name || ''
                  const institution = event.toInstitution?.name || ''
                  return `${propertyCode} - ${patternName} | ${institution}`
                })
                .join(', ')
            }

            // Se for do tipo 'calibration', adiciona o nome do supplier ao final
            let fullName = baseName
            // Calibração: adiciona nome do fornecedor
            if (
              event.schedulingEventType === 2 &&
              event.supplier &&
              event.supplier.name
            ) {
              fullName = baseName
                ? `${baseName} ${event.supplier.name}`
                : event.supplier.name
            }
            // Translado para fornecedor: adiciona nome do fornecedor
            if (
              event.schedulingEventType === 1 &&
              event.supplier &&
              event.supplier.name
            ) {
              fullName = baseName
                ? `${baseName} ${event.supplier.name}`
                : event.supplier.name
            }

            // Cor baseada no tipo de evento
            const typeColor = this.getColorByEventType(
              event.schedulingEventType
            )

            return {
              ...event,
              name: fullName,
              schedulingEventType: event.schedulingEventType,
              color: typeColor,
              start: event.startDate.split('T')[0],
              end: event.endDate.split('T')[0],
              startDate: event.startDate,
              endDate: event.endDate,
              timed: false,
            }
          })

          this.allEvents = parsedEvents
          this.events = [...parsedEvents]

          this.filterEventsByPattern()
        })
    },

    filterEventsByPattern() {
      if (!this.allEvents || !this.allEvents.length) {
        this.events = []
        return
      }

      if (this.selectedPatterns && this.selectedPatterns.length > 0) {
        const selectedIds = this.selectedPatterns.map((p) => p.id)

        this.events = this.allEvents.filter((event) =>
          event.schedulingPatterns?.some((sp) =>
            selectedIds.includes(sp.patternId)
          )
        )
        return
      }

      if (this.searchPattern && this.calibrationV2Patterns?.length) {
        const patternIds = this.calibrationV2Patterns.map((p) => p.id)

        this.events = this.allEvents.filter((event) =>
          event.schedulingPatterns?.some((sp) =>
            patternIds.includes(sp.patternId)
          )
        )
        return
      }

      this.events = [...this.allEvents]
    },

    async getMiniCalendarScheduling(year = null, month = null) {
      // Se não foram passados ano/mês, usa a data do miniCalendar
      if (year === null || month === null) {
        year = this.miniCalendarDate.getFullYear()
        month = this.miniCalendarDate.getMonth()
      }

      const startDate = new Date(year, month, 1)
      const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999)

      const filter = {
        institutionId: this.institution.id,
        startDate: this.getDateString(startDate),
        endDate: this.getDateString(endDate),
      }

      try {
        const response = await this.apiService.post(
          `scheduling/list-by-instituitionid-mini-calendar`,
          filter
        )
        const miniEvents = response.content.map((event) => ({
          ...event,
          name: event.pattern ? event.pattern.name : '',
          start: event.startDate.split('T')[0],
          end: event.endDate.split('T')[0],
          timed: false,
        }))

        this.miniCalendarEvents = miniEvents
      } catch (error) {
        this.$toast.error('Erro ao obter agendamentos do mini calendário')
      }
    },

    async handleSaveEvent({ eventForm, editingEvent }) {
      const timezoneHeader = {
        headers: {
          institutiontimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }

      try {
        const schedulingPatterns =
          eventForm.calibrationPattern?.length > 0
            ? eventForm.calibrationPattern
            : this.selectedPatterns?.map((p) => p.id) ?? []

        if (schedulingPatterns.length === 0) {
          this.$toast.error('Selecione pelo menos um padrão para agendar')
          return
        }

        if (editingEvent && eventForm.id) {
          const schedulingEdit = {
            id: eventForm.id,
            fromInstitutionId: this.institution.id,
            toInstitutionId: eventForm.destinationInstitution,
            toUnidadeId: eventForm.destinationUnidade,
            alertDate: new Date(eventForm.alertDateTime),
            startDate: new Date(eventForm.startDateTime),
            endDate: new Date(eventForm.endDateTime),
            schedulingEventType: eventForm.schedulingEventType,
            details: eventForm.details,
            supplierId: eventForm.supplierId,
            patternIds: schedulingPatterns,
          }

          await this.apiService.put(
            'scheduling/edit',
            schedulingEdit,
            timezoneHeader
          )
          this.$toast.success('Agendamento atualizado com sucesso!')
        } else {
          const schedulingAdd = {
            institutionId: this.institution.id,
            toUnidadeId: eventForm.destinationUnidade,
            fromInstitutionId: this.institution.id,
            toInstitutionId: eventForm.destinationInstitution,
            userId: this.user.id,
            startDate: new Date(eventForm.startDateTime),
            endDate: new Date(eventForm.endDateTime),
            alertDate: new Date(eventForm.alertDateTime),
            schedulingEventType: eventForm.schedulingEventType,
            details: eventForm.details,
            supplierId: eventForm.supplierId,
            patternIds: schedulingPatterns,
          }

          await this.apiService.post(
            'scheduling/add',
            schedulingAdd,
            timezoneHeader
          )
          this.$toast.success('Agendamento criado! 📅')
        }
        this.showEventDialog = false

        this.getScheduling()
        this.getMiniCalendarScheduling()
        this.editingEvent = false
        this.selectedPatterns = []
      } catch (error) {
        this.$toast.error
      }
    },
    getManufacturers() {
      this.manufacturerLoading = true
      this.apiService
        .get(`manufacturer/list/${this.institution.id}`)
        .then((response) => {
          this.manufacturers = response.content
          this.manufacturerLoading = false
        })
        .catch(() => {
          this.manufacturerLoading = false
        })
    },
    removeScheduling(event) {
      this.apiService
        .delete(`scheduling/delete/${event.id}`)
        .then(() => {
          this.$toast.success('Agendamento removido com sucesso!')
          this.getScheduling()
          this.getMiniCalendarScheduling()
        })
        .catch((error) => {
          console.error('Erro ao remover agendamento', error)
          this.$toast.error('Erro ao remover agendamento')
        })
    },
    async getUnidades(institutionId) {
      await this.apiService
        .get(`unidade/list/${institutionId}`)
        .then((response) => {
          this.unidades = response.content
        })
    },
    getFilteredPatterns() {
      // Se não tiver nada digitado, volta pra lista completa de padrões
      if (!this.searchPattern || !this.searchPattern.trim()) {
        this.getPattern()
        // Quando limpa a busca, mostra todos os eventos
        this.selectedPatterns = []
        this.filterEventsByPattern()
        return
      }

      // Quebra o texto digitado em termos (suporta vírgula, ponto e vírgula e espaço)
      let terms = this.searchPattern
        .split(/[,;\s]+/)
        .map((t) => t.trim())
        .filter((t) => t)

      // Se depois de quebrar não sobrou nada, volta pra lista padrão
      if (terms.length === 0) {
        this.getPattern()
        this.selectedPattern = null
        this.filterEventsByPattern()
        return
      }

      // Monta o payload pro back (casando com o filtro que você montou no C#)
      const payload = {
        institutionId: this.institution.id,
        Search: this.searchPattern,
        Page: 1,
        PageSize: 50,
        Names: terms,
        SerialNumbers: terms,
        PropertyCodes: terms,
      }

      this.apiService
        .post('calibration/v2/pattern/scheduling-requests', payload)
        .then((response) => {
          const content = response.content || {}

          const patterns = Array.isArray(content.data) ? content.data : []

          this.calibrationV2Patterns = patterns

          this.filterEventsByPattern()

          if (patterns.length > 0) {
            this.$toast.info(`${patterns.length} padrão(ões) encontrado(s)`)
          } else {
            this.$toast.info('Nenhum padrão encontrado')
          }
        })
        .catch((error) => {
          console.error('Erro ao buscar padrões', error)
          this.$toast.error('Erro ao buscar padrões')
        })
    },

    async getPattern() {
      await this.apiService
        .get(`calibration/v2/pattern/list/${this.institution.id}`)
        .then((response) => {
          this.calibrationV2Patterns = response.content
        })
    },
    filterTable() {
      this.page = 1
      this.filter.page = this.page
      this.filter.pageSize = this.pageSize
      this.filter.manufacturer = this.patternManufacturer
      this.filter.patternModel = this.patternModel
      this.filter.alienated = this.patternAlienated
      this.list()
    },
    async list() {
      this.filter.institutionId = this.institution.Id
      this.filter.unidadeId = this.unidadeId

      await this.apiService
        .post('calibration/v2/pattern/list', this.filter)
        .then((response) => {
          this.calibrationV2Patterns = response.content.data
        })
        .finally(() => {
          this.showFilter = false
        })
    },
    reset() {
      this.filter = {
        institutionId: this.institution.Id,
        unidadeId: null,
        propertyCode: null,
        manufacturer: null,
        serialNumber: null,
        model: null,
        alienated: null,
      }
      this.institutionId = null
      this.unidadeId = null
      this.patternManufacturer = null
      this.patternModel = null
      this.patternAlienated = null

      this.list()
    },
    viewDay({ date }) {
      this.focus = date
      this.type = 'day'
    },

    getEventColor(event) {
      const rgb = parseInt(event.color.substring(1), 16)
      const r = (rgb >> 16) & 0xff
      const g = (rgb >> 8) & 0xff
      const b = (rgb >> 0) & 0xff

      return event === this.dragEvent
        ? `rgba(${r}, ${g}, ${b}, 0.7)`
        : event === this.createEvent
        ? `rgba(${r}, ${g}, ${b}, 0.7)`
        : event.color
    },

    setToday() {
      const today = new Date()
      this.focus = this.getDateString(today)
    },

    nth(d) {
      return d > 3 && d < 21
        ? 'th'
        : ['th', 'st', 'nd', 'rd', 'th', 'th', 'th', 'th', 'th', 'th'][d % 10]
    },

    monthFormatter(date) {
      try {
        if (!date || (!date.year && !date.month)) {
          console.error('Objeto de data inválido em monthFormatter:', date)
          return new Intl.DateTimeFormat('pt-BR', { month: 'long' }).format(
            new Date()
          )
        }

        // Validate year and month values
        if (
          isNaN(date.year) ||
          isNaN(date.month) ||
          date.month < 1 ||
          date.month > 12
        ) {
          console.error('Ano ou mês inválido em monthFormatter:', date)
          return new Intl.DateTimeFormat('pt-BR', { month: 'long' }).format(
            new Date()
          )
        }

        return new Intl.DateTimeFormat('pt-BR', { month: 'long' }).format(
          new Date(date.year, date.month - 1)
        )
      } catch (error) {
        console.error('Erro em monthFormatter:', error, date)
        return new Intl.DateTimeFormat('pt-BR', { month: 'long' }).format(
          new Date()
        )
      }
    },

    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event
        this.selectedElement = nativeEvent.target
        requestAnimationFrame(() =>
          requestAnimationFrame(() => (this.selectedOpen = true))
        )
      }

      if (this.selectedOpen) {
        this.selectedOpen = false
        requestAnimationFrame(() => requestAnimationFrame(() => open()))
      } else {
        open()
      }

      nativeEvent.stopPropagation()
    },

    openEventDetails(event) {
      if (!event || !event.id) return

      this.selectedEvent = event
      this.selectedOpen = true
    },

    updateRange({ start, end }) {
      // guarda o range visível para o título
      this.start = start
      this.end = end

      // monta datas JS para buscar os eventos
      const startDate = new Date(`${start.date}T00:00:00`)
      const endDate = new Date(`${end.date}T23:59:59`)

      if (this.type === 'month') {
        startDate.setDate(startDate.getDate() - 6)
        endDate.setDate(endDate.getDate() + 6)
      }

      this.getScheduling(startDate, endDate)
    },

    editEvent(event) {
      this.selectedOpen = false
      this.editingEvent = true

      this.eventForm = {
        id: event.id,
        calibrationPattern:
          event.schedulingPatterns?.map((p) => p.patternId) || [],
        destinationInstitution: event.toInstitutionId,
        destinationUnidade: event.toUnidadeId,
        startDateTime: new Date(event.startDate),
        endDateTime: new Date(event.endDate),
        schedulingEventType:
          typeof event.schedulingEventType === 'number'
            ? event.schedulingEventType
            : 0,
        details: event.details || '',
        supplierId: event.supplierId || null,
        alert: !!event.alertDate,
        alertDateTime: event.alertDate ? new Date(event.alertDate) : null,
      }

      this.showEventDialog = true
    },

    getDateString(date) {
      if (!date || isNaN(date.getTime())) {
        console.error('Invalid date passed to getDateString:', date)
        return new Date().toISOString().substr(0, 10) // Return today's date as fallback
      }
      return date.toISOString().substr(0, 10)
    },

    onMiniCalendarClick({ date }) {
      this.focus = date
      this.type = 'day'
    },

    hasEventsOnDate(date) {
      return this.visibleEvents.some((event) => event.start === date)
    },

    selectPattern(pattern) {
      const isSelected = this.selectedPattern?.id === pattern.id

      this.selectedPattern = isSelected ? null : pattern

      this.filterEventsByPattern()
    },

    applyPatternSearchFilter() {
      if (!this.searchPattern || this.searchPattern.trim() === '') {
        this.clearPatternSearch()
        return
      }

      this.activeSearchFilter = this.searchPattern.trim()
      this.getFilteredPatterns()
      this.filterEventsByPattern()

      const matchCount = this.events.length
      this.$toast.info(`${matchCount} agendamento(s) encontrado(s)`)
    },

    clearPatternSearch() {
      this.searchPattern = ''
      this.activeSearchFilter = null
      this.selectedPatterns = []
      this.getPattern()
      this.getScheduling()
      this.filterEventsByPattern()
    },

    // Métodos do Mini Calendário
    previousMonth() {
      if (!this.miniCalendarDate || isNaN(this.miniCalendarDate.getTime())) {
        this.miniCalendarDate = new Date()
        return
      }
      const newDate = new Date(this.miniCalendarDate)
      newDate.setMonth(newDate.getMonth() - 1)
      this.miniCalendarDate = newDate

      this.getMiniCalendarScheduling(newDate.getFullYear(), newDate.getMonth())
    },

    nextMonth() {
      if (!this.miniCalendarDate || isNaN(this.miniCalendarDate.getTime())) {
        this.miniCalendarDate = new Date()
        return
      }
      const newDate = new Date(this.miniCalendarDate)
      newDate.setMonth(newDate.getMonth() + 1)
      this.miniCalendarDate = newDate

      this.getMiniCalendarScheduling(newDate.getFullYear(), newDate.getMonth())
    },

    selectDate(day) {
      if (!day || !day.date || isNaN(day.date.getTime())) {
        console.error('Data inválida selecionada:', day)
        return
      }

      try {
        if (!day.otherMonth) {
          this.selectedMiniDate = new Date(day.date)
          // Navigate main calendar to selected date
          const dateString = this.getDateString(day.date)
          this.focus = dateString
          this.type = 'day'

          // Show info about events on this day
          if (day.hasEvents && day.events.length > 0) {
            const eventNames = day.events
              .map((e) => e.name || e.calibrationPatternName)
              .join(', ')
            this.$toast.info(
              `${day.events.length} evento(s) em ${day.day}: ${eventNames}`
            )
          }
        } else {
          // If clicking on other month day, navigate to that month
          this.miniCalendarDate = new Date(day.date)
          this.selectedMiniDate = new Date(day.date)
          const dateString = this.getDateString(day.date)
          this.focus = dateString
          this.type = 'day'
        }
      } catch (error) {
        console.error('Error in selectDate:', error, day)
      }
    },

    isSameDate(date1, date2) {
      if (
        !date1 ||
        !date2 ||
        isNaN(date1.getTime()) ||
        isNaN(date2.getTime())
      ) {
        return false
      }
      return date1.toDateString() === date2.toDateString()
    },

    getEventsForDate(date, source = 'mini') {
      const targetDate = new Date(date)
      const targetDateString = this.getDateString(targetDate)
      const sourceEvents =
        source === 'mini' ? this.miniCalendarEvents : this.visibleEvents

      return sourceEvents.filter((event) => {
        // For timestamp-based events
        if (typeof event.start === 'number') {
          const eventDate = new Date(event.start)
          return this.getDateString(eventDate) === targetDateString
        }
        // For string-based events (backward compatibility)
        return (
          event.start === targetDateString ||
          event.startDate === targetDateString
        )
      })
    },

    // Drag and Drop Methods
    onPatternDragStart(event, pattern) {
      const selected = this.selectedPatterns.some((p) => p.id === pattern.id)
        ? this.selectedPatterns
        : [pattern]

      this.draggedPattern = pattern

      event.dataTransfer.setData('application/json', JSON.stringify(selected))
      event.dataTransfer.effectAllowed = 'copy'
      event.target.style.opacity = '0.5'
    },

    onPatternDragEnd(event) {
      // Reset visual feedback
      event.target.style.opacity = '1'
      this.draggedPattern = null
      this.dragOverDate = null
    },

    onCalendarDragOver(event) {
      event.preventDefault()

      if (this.draggedEvent) {
        event.dataTransfer.dropEffect = 'move'
      } else {
        event.dataTransfer.dropEffect = 'copy'
      }

      const targetElement =
        event.target.closest('[data-date]') ||
        event.target.closest('.v-calendar-daily__day') ||
        event.target.closest('.v-calendar-monthly__day') ||
        event.target.closest('.v-calendar-weekly__day') ||
        event.target

      let hoverDate = null

      if (targetElement) {
        const dayContainer =
          targetElement.closest('.v-calendar-monthly__day') ||
          targetElement.closest('.v-calendar-weekly__day') ||
          targetElement.closest('.v-calendar-daily__day') ||
          targetElement
        const dateSpan = dayContainer.querySelector('[data-date]')
        if (dateSpan) {
          hoverDate = dateSpan.getAttribute('data-date')
        }

        // Dia externo (outro mês) — não possui [data-date], ignorar
        if (!hoverDate && dayContainer !== targetElement && !dateSpan) {
          this.dragOverDate = null
          return
        }

        if (!hoverDate) {
          hoverDate = this.getDateFromElement(targetElement, event) || null
        }

        if (!hoverDate && this.start && this.start.date) {
          const monthlyDayContainer = targetElement.closest(
            '.v-calendar-monthly__day'
          )
          if (monthlyDayContainer) {
            const allMonthlyDays = document.querySelectorAll(
              '.v-calendar-monthly__day'
            )
            const dayIndex =
              Array.from(allMonthlyDays).indexOf(monthlyDayContainer)

            if (dayIndex >= 0) {
              const startDate = new Date(this.start.date)
              const targetDate = new Date(startDate)
              targetDate.setDate(startDate.getDate() + dayIndex)
              hoverDate = targetDate.toISOString().split('T')[0]
            }
          }

          if (!hoverDate) {
            const weeklyDayContainer = targetElement.closest(
              '.v-calendar-weekly__day'
            )
            if (weeklyDayContainer) {
              const allWeeklyDays = document.querySelectorAll(
                '.v-calendar-weekly__day'
              )
              const dayIndex =
                Array.from(allWeeklyDays).indexOf(weeklyDayContainer)

              if (dayIndex >= 0) {
                const startDate = new Date(this.start.date)
                const targetDate = new Date(startDate)
                targetDate.setDate(startDate.getDate() + dayIndex)
                hoverDate = targetDate.toISOString().split('T')[0]
              }
            }
          }
        }
      }

      this.dragOverDate = hoverDate

      if (
        this.lastHighlightedDay &&
        this.lastHighlightedDay !== targetElement
      ) {
        this.lastHighlightedDay.classList.remove('day-drag-target')
        this.lastHighlightedDay = null
      }

      if (targetElement && hoverDate) {
        targetElement.classList.add('day-drag-target')
        this.lastHighlightedDay = targetElement
      }
    },

    onCalendarDrop(event) {
      event.preventDefault()
      event.stopPropagation()

      if (this.draggedEvent) {
        this.handleEventDrop(event)
        return
      }

      try {
        const patternData = JSON.parse(
          event.dataTransfer.getData('application/json')
        )

        const targetElement =
          event.target.closest('[data-date]') ||
          event.target.closest('.v-calendar-daily__day') ||
          event.target.closest('.v-calendar-monthly__day') ||
          event.target.closest('.v-calendar-weekly__day') ||
          event.target

        let dropDate = this.dragOverDate || this.focus

        if (targetElement) {
          const dayContainer =
            targetElement.closest('.v-calendar-monthly__day') ||
            targetElement.closest('.v-calendar-weekly__day') ||
            targetElement.closest('.v-calendar-daily__day') ||
            targetElement
          const dateSpan = dayContainer.querySelector('[data-date]')
          if (dateSpan) {
            dropDate = dateSpan.getAttribute('data-date')
          } else {
            dropDate = this.getDateFromElement(targetElement, event) || dropDate
          }
        }

        const patterns = Array.isArray(patternData)
          ? patternData
          : [patternData]
        patterns.forEach((p) => this.createEventFromPattern(p, dropDate))
      } catch (error) {
        this.$toast.error('Erro ao processar o item arrastado')
      } finally {
        if (this.lastHighlightedDay) {
          this.lastHighlightedDay.classList.remove('day-drag-target')
          this.lastHighlightedDay = null
        }

        this.dragOverDate = null
        this.draggedPattern = null
      }
    },
    onCalendarDragLeave(event) {
      // Only clear if we're leaving the calendar area completely
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.dragOverDate = null

        if (this.lastHighlightedDay) {
          this.lastHighlightedDay.classList.remove('day-drag-target')
          this.lastHighlightedDay = null
        }
      }
    },

    getDateFromElement(element, event) {
      let dateElement = element.closest('button.v-btn')

      if (dateElement) {
        const ariaLabel = dateElement.getAttribute('aria-label')
        if (ariaLabel) {
          const match = ariaLabel.match(
            /(\d{1,2})\s+de\s+([\p{L}]+)\s+de\s+(\d{4})/iu
          )
          if (match) {
            const day = match[1].padStart(2, '0')
            const monthName = match[2].toLowerCase()
            const year = match[3]

            const monthMap = {
              janeiro: '01',
              fevereiro: '02',
              março: '03',
              abril: '04',
              maio: '05',
              junho: '06',
              julho: '07',
              agosto: '08',
              setembro: '09',
              outubro: '10',
              novembro: '11',
              dezembro: '12',
            }

            const month = monthMap[monthName]
            if (month) {
              const dateStr = `${year}-${month}-${day}`
              return dateStr
            }
          }
        }
      }

      const dayContainer = element.closest('.v-calendar-monthly__day')
      if (dayContainer) {
        const dayButton = dayContainer.querySelector('button.v-btn')
        if (dayButton) {
          const ariaLabel = dayButton.getAttribute('aria-label')

          if (ariaLabel) {
            const match = ariaLabel.match(
              /(\d{1,2})\s+de\s+([\p{L}]+)\s+de\s+(\d{4})/iu
            )
            if (match) {
              const day = match[1].padStart(2, '0')
              const monthName = match[2].toLowerCase()
              const year = match[3]

              const monthMap = {
                janeiro: '01',
                fevereiro: '02',
                março: '03',
                abril: '04',
                maio: '05',
                junho: '06',
                julho: '07',
                agosto: '08',
                setembro: '09',
                outubro: '10',
                novembro: '11',
                dezembro: '12',
              }

              const month = monthMap[monthName]
              if (month) {
                const dateStr = `${year}-${month}-${day}`
                return dateStr
              }
            }
          }

          const buttonText = dayButton.textContent.trim()
          const dayNum = parseInt(buttonText)

          if (!isNaN(dayNum) && this.start) {
            const year = this.start.year
            const month = this.start.month
            const dateStr = `${year}-${month
              .toString()
              .padStart(2, '0')}-${dayNum.toString().padStart(2, '0')}`
            return dateStr
          }
        }
      }

      const weeklyDayContainer = element.closest('.v-calendar-weekly__day')
      if (weeklyDayContainer) {
        const dataDateAttr = weeklyDayContainer.getAttribute('data-date')
        if (dataDateAttr) {
          return dataDateAttr
        }

        let headElement = weeklyDayContainer.querySelector(
          '.v-calendar-weekly__day-label button.v-btn'
        )

        if (!headElement) {
          headElement = weeklyDayContainer.querySelector(
            '.v-calendar-weekly__day-label button'
          )
        }

        if (!headElement) {
          headElement = weeklyDayContainer.querySelector('button.v-btn')
        }

        if (!headElement) {
          headElement = weeklyDayContainer.querySelector('button[aria-label]')
        }

        if (headElement) {
          const ariaLabel = headElement.getAttribute('aria-label')
          if (ariaLabel) {
            const match = ariaLabel.match(
              /(\d{1,2})\s+de\s+([\p{L}]+)\s+de\s+(\d{4})/iu
            )
            if (match) {
              const day = match[1].padStart(2, '0')
              const monthName = match[2].toLowerCase()
              const year = match[3]

              const monthMap = {
                janeiro: '01',
                fevereiro: '02',
                março: '03',
                abril: '04',
                maio: '05',
                junho: '06',
                julho: '07',
                agosto: '08',
                setembro: '09',
                outubro: '10',
                novembro: '11',
                dezembro: '12',
              }

              const month = monthMap[monthName]
              if (month) {
                const dateStr = `${year}-${month}-${day}`
                return dateStr
              }
            }
          }
        }

        if (this.start && this.start.date) {
          const allWeeklyDays = document.querySelectorAll(
            '.v-calendar-weekly__day'
          )
          const dayIndex = Array.from(allWeeklyDays).indexOf(weeklyDayContainer)

          if (dayIndex >= 0) {
            const startDate = new Date(this.start.date)
            const targetDate = new Date(startDate)
            targetDate.setDate(startDate.getDate() + dayIndex)
            const calculatedDate = targetDate.toISOString().split('T')[0]

            return calculatedDate
          }
        }
      }

      const dailyDayContainer = element.closest('.v-calendar-daily__day')
      if (dailyDayContainer) {
        const dataDateAttr = dailyDayContainer.getAttribute('data-date')
        if (dataDateAttr) {
          return dataDateAttr
        }
      }

      if (this.start && this.start.date) {
        return null
      }

      const fallbackDate = this.focus || this.getDateString(new Date())
      if (this.start && this.end) {
        return fallbackDate
      }

      return fallbackDate
    },

    calculateDateFromPosition(event) {
      if (this.$refs.calendar && this.$refs.calendar.$el) {
        try {
          const calendar = this.$refs.calendar
          const calendarEl = calendar.$el
          const rect = calendarEl.getBoundingClientRect()

          if (
            rect &&
            event &&
            typeof event.clientY === 'number' &&
            typeof calendar.yToTime === 'function'
          ) {
            const y = event.clientY - rect.top
            const minutes = calendar.yToTime(y)

            if (typeof minutes === 'number' && !isNaN(minutes)) {
              let baseDate = this.focus ? new Date(this.focus) : new Date()
              if (isNaN(baseDate.getTime())) {
                baseDate = new Date()
              }

              const hours = Math.floor(minutes / 60)
              const mins = Math.floor(minutes % 60)
              baseDate.setHours(hours, mins, 0, 0)

              return this.getDateString(baseDate)
            }
          }
        } catch (error) {
          console.error('Failed to calculate date from position:', error)
        }
      }

      return this.focus || this.getDateString(new Date())
    },

    createEventFromPattern(pattern, dropDate) {
      // Ensure we have a valid date
      const targetDate = new Date(dropDate || this.focus)
      if (isNaN(targetDate.getTime())) {
        this.$toast.error('Data inválida para criar agendamento')
        return
      }

      // Set default time (9 AM to 11 AM)
      const startDateTime = new Date(targetDate)
      startDateTime.setHours(9, 0, 0, 0)

      const endDateTime = new Date(targetDate)
      endDateTime.setHours(11, 0, 0, 0)

      const patterns = [pattern]

      this.eventForm = {
        name: '',
        startDateTime: startDateTime,
        endDateTime: endDateTime,
        calibrationPattern: [pattern.id],
        destinationInstitution: null,
        schedulingEventType: 2, // Calibração
        color: '#5b5b5b',
        details: '',
      }

      // Focus on the target date
      this.focus = this.getDateString(targetDate)

      // Open the event dialog
      this.editingEvent = false
      this.showEventDialog = true

      // Show info message
      const nomes = patterns.map((p) => p.name).join(', ')
      this.$toast.info(`Padrão(s): ${nomes} selecionado(s).`)
    },

    // Calendar Event Drag Methods (based on calendar-example.vue)
    startDrag({ event, timed }) {
      if (event && timed) {
        this.dragEvent = event
        this.dragTime = null
        this.extendOriginal = null
      }
    },

    startTime(tms) {
      const mouse = this.toTime(tms)

      if (this.dragEvent && this.dragTime === null) {
        const start = this.dragEvent.start
        this.dragTime = mouse - start
      } else {
        this.createStart = this.roundTime(mouse)
        this.createEvent = {
          name: `Evento #${this.events.length + 1}`,
          color: this.rndElement([
            '#f44336',
            '#e91e63',
            '#9c27b0',
            '#673ab7',
            '#3f51b5',
            '#2196f3',
            '#03a9f4',
            '#00bcd4',
            '#009688',
            '#4caf50',
            '#8bc34a',
            '#cddc39',
            '#ffeb3b',
            '#ffc107',
            '#ff9800',
            '#ff5722',
          ]),
          start: this.createStart,
          end: this.createStart,
          timed: true,
          // Add default properties for compatibility
          startDateTime: new Date(this.createStart),
          endDateTime: new Date(this.createStart),
          startDate: this.getDateString(new Date(this.createStart)),
          endDate: this.getDateString(new Date(this.createStart)),
          startHour: this.formatTimeFromTimestamp(this.createStart),
          endHour: this.formatTimeFromTimestamp(this.createStart),
          calibrationPattern: null,
          calibrationPatternName: '',
          destinationInstitution: null,
          destinationInstitutionName: '',
          details: 'Evento criado por arrastar e soltar',
        }

        this.events.push(this.createEvent)
      }
    },

    extendBottom(event) {
      this.createEvent = event
      this.createStart = event.start
      this.extendOriginal = event.end
    },

    mouseMove(tms) {
      const mouse = this.toTime(tms)

      if (this.dragEvent && this.dragTime !== null) {
        const start = this.dragEvent.start
        const end = this.dragEvent.end
        const duration = end - start
        const newStartTime = mouse - this.dragTime
        const newStart = this.roundTime(newStartTime)
        const newEnd = newStart + duration

        this.dragEvent.start = newStart
        this.dragEvent.end = newEnd

        // Update helper properties
        this.updateEventTimes(this.dragEvent, newStart, newEnd)
      } else if (this.createEvent && this.createStart !== null) {
        const mouseRounded = this.roundTime(mouse, false)
        const min = Math.min(mouseRounded, this.createStart)
        const max = Math.max(mouseRounded, this.createStart)

        this.createEvent.start = min
        this.createEvent.end = max

        // Update helper properties
        this.updateEventTimes(this.createEvent, min, max)
      }
    },

    endDrag() {
      this.dragTime = null
      this.dragEvent = null
      this.createEvent = null
      this.createStart = null
      this.extendOriginal = null
    },

    cancelDrag() {
      if (this.createEvent) {
        if (this.extendOriginal) {
          this.createEvent.end = this.extendOriginal
          this.updateEventTimes(
            this.createEvent,
            this.createEvent.start,
            this.extendOriginal
          )
        } else {
          const i = this.events.indexOf(this.createEvent)
          if (i !== -1) {
            this.events.splice(i, 1)
          }
        }
      }

      this.createEvent = null
      this.createStart = null
      this.dragTime = null
      this.dragEvent = null
    },

    clickDate(date) {
      if (!this.dragEvent) {
        this.viewDay(date)
      }
    },

    // Helper methods for time calculations
    roundTime(time, down = true) {
      const roundTo = 15 // minutes
      const roundDownTime = roundTo * 60 * 1000

      return down
        ? time - (time % roundDownTime)
        : time + (roundDownTime - (time % roundDownTime))
    },

    toTime(tms) {
      return new Date(
        tms.year,
        tms.month - 1,
        tms.day,
        tms.hour,
        tms.minute
      ).getTime()
    },

    formatTimeFromTimestamp(timestamp) {
      const date = new Date(timestamp)
      return `${date.getHours().toString().padStart(2, '0')}:${date
        .getMinutes()
        .toString()
        .padStart(2, '0')}`
    },

    updateEventTimes(event, start, end) {
      const startDate = new Date(start)
      const endDate = new Date(end)

      event.startDateTime = startDate
      event.endDateTime = endDate
      event.startDate = this.getDateString(startDate)
      event.endDate = this.getDateString(endDate)
      event.startHour = this.formatTimeFromTimestamp(start)
      event.endHour = this.formatTimeFromTimestamp(end)
    },

    rndElement(arr) {
      return arr[Math.floor(Math.random() * arr.length)]
    },

    // Métodos de navegação personalizados
    previousPeriod() {
      this.$refs.calendar.prev()
    },

    nextPeriod() {
      this.$refs.calendar.next()
    },

    onNavZoneEnter(direction) {
      this.dragNavZone = direction
      clearTimeout(this._navZoneTimer)
      this._navZoneTimer = setTimeout(() => {
        if (direction === 'left') {
          this.$refs.calendar.prev()
        } else {
          this.$refs.calendar.next()
        }
        this.dragNavZone = null
      }, 700)
    },

    onNavZoneLeave() {
      clearTimeout(this._navZoneTimer)
      this.dragNavZone = null
    },

    // Método do date-picker de navegação
    navigateToSelectedDate(date) {
      if (date) {
        this.miniCalendarDate = new Date(date)
        this.getMiniCalendarScheduling(
          this.miniCalendarDate.getFullYear(),
          this.miniCalendarDate.getMonth()
        )
      }
    },

    onEventDragStart(dragEvent, calendarEvent) {
      if (!calendarEvent.id) return

      dragEvent.dataTransfer.effectAllowed = 'move'
      dragEvent.dataTransfer.setData('text/plain', calendarEvent.id.toString())

      const dragEl = dragEvent.currentTarget || dragEvent.target

      const ghost = document.createElement('div')
      const eventColor = calendarEvent.color || '#1976d2'
      ghost.innerHTML = `
        <span style="font-size:12px;color:white;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:160px;display:block">
          ${calendarEvent.name || 'Evento'}
        </span>
      `
      ghost.style.cssText = `
        position: fixed;
        top: -1000px;
        left: -1000px;
        background: ${eventColor};
        color: white;
        padding: 6px 12px;
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        font-size: 12px;
        font-family: sans-serif;
        pointer-events: none;
        z-index: 9999;
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 120px;
        max-width: 200px;
      `
      document.body.appendChild(ghost)
      dragEvent.dataTransfer.setDragImage(ghost, 16, 16)
      setTimeout(() => document.body.removeChild(ghost), 0)

      if (dragEl && dragEl.style) {
        dragEl.style.opacity = '0.35'
      }

      this.draggedEvent = {
        id: calendarEvent.id,
        name: calendarEvent.name,
        color: calendarEvent.color,
        start: calendarEvent.start,
        end: calendarEvent.end,
        startDate: calendarEvent.startDate,
        endDate: calendarEvent.endDate,
        schedulingEventType: calendarEvent.schedulingEventType,
        fromInstitutionId: calendarEvent.fromInstitutionId,
        toInstitutionId: calendarEvent.toInstitutionId,
        toUnidadeId: calendarEvent.toUnidadeId,
        supplierId: calendarEvent.supplierId,
        alertDate: calendarEvent.alertDate,
        details: calendarEvent.details,
        schedulingPatterns: calendarEvent.schedulingPatterns
          ? JSON.parse(JSON.stringify(calendarEvent.schedulingPatterns))
          : [],
      }

      const startDate = new Date(calendarEvent.start)
      const endDate = new Date(calendarEvent.end)

      const startNormalized = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate()
      )
      const endNormalized = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate()
      )

      const diffMs = endNormalized - startNormalized
      this.draggedEventDuration = Math.round(diffMs / (1000 * 60 * 60 * 24))
    },

    onEventDragEnd(dragEvent) {
      const dragEl = dragEvent.currentTarget || dragEvent.target
      if (dragEl && dragEl.style) {
        dragEl.style.opacity = '1'
      }

      if (this.lastHighlightedDay) {
        this.lastHighlightedDay.classList.remove('day-drag-target')
        this.lastHighlightedDay = null
      }

      setTimeout(() => {
        this.dragOverDate = null
        this.draggedEvent = null
        this.draggedEventDuration = 0
      }, 150)
    },

    async handleEventDrop(dropEvent) {
      if (!this.draggedEvent) return

      dropEvent.preventDefault()
      dropEvent.stopPropagation()

      try {
        let dropDate = this.dragOverDate

        if (!dropDate) {
          dropDate = this.calculateDateFromMousePosition(
            dropEvent.clientX,
            dropEvent.clientY
          )

          if (!dropDate) {
            this.$toast.error('Não foi possível detectar a data de destino')
            return
          }
        }

        const newStartDate = new Date(dropDate + 'T00:00:00')
        const newEndDate = new Date(newStartDate)
        newEndDate.setDate(newEndDate.getDate() + this.draggedEventDuration)

        await this.updateEventDates(this.draggedEvent, newStartDate, newEndDate)
      } catch (error) {
        this.$toast.error('Erro ao mover agendamento: ' + error.message)
        await this.reloadScheduling()
      } finally {
        if (this.lastHighlightedDay) {
          this.lastHighlightedDay.classList.remove('day-drag-target')
          this.lastHighlightedDay = null
        }

        setTimeout(() => {
          this.draggedEvent = null
          this.draggedEventDuration = 0
          this.dragOverDate = null
        }, 100)
      }
    },

    calculateDateFromMousePosition(clientX, clientY) {
      try {
        const calendarEl = this.$refs.calendar?.$el
        if (!calendarEl) return null

        let daySelector = '.v-calendar-weekly__day'
        if (this.type === 'month') {
          daySelector = '.v-calendar-monthly__day'
        } else if (this.type === 'day') {
          daySelector = '.v-calendar-daily__day'
        }

        const dayElements = calendarEl.querySelectorAll(daySelector)
        if (!dayElements || dayElements.length === 0) return null

        let closestDay = null
        let minDistance = Infinity

        dayElements.forEach((dayEl) => {
          const rect = dayEl.getBoundingClientRect()

          if (
            clientX >= rect.left &&
            clientX <= rect.right &&
            clientY >= rect.top &&
            clientY <= rect.bottom
          ) {
            closestDay = dayEl
            minDistance = 0
          } else {
            const centerX = rect.left + rect.width / 2
            const centerY = rect.top + rect.height / 2
            const distance = Math.sqrt(
              Math.pow(clientX - centerX, 2) + Math.pow(clientY - centerY, 2)
            )

            if (distance < minDistance) {
              minDistance = distance
              closestDay = dayEl
            }
          }
        })

        if (!closestDay) return null

        const dateSpan = closestDay.querySelector('[data-date]')
        if (dateSpan) {
          return dateSpan.getAttribute('data-date')
        }

        const dateFromElement = this.getDateFromElement(closestDay, {
          clientX,
          clientY,
        })

        if (dateFromElement) {
          return dateFromElement
        }

        if (this.start && this.start.date) {
          const dayIndex = Array.from(dayElements).indexOf(closestDay)
          const startDate = new Date(this.start.date)
          const targetDate = new Date(startDate)
          targetDate.setDate(startDate.getDate() + dayIndex)
          return targetDate.toISOString().split('T')[0]
        }

        return null
      } catch (error) {
        return null
      }
    },

    async updateEventDates(event, newStartDate, newEndDate) {
      const timezoneHeader = {
        headers: {
          institutiontimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }

      try {
        const originalStart = new Date(event.startDate || event.start)
        const originalEnd = new Date(event.endDate || event.end)

        const finalStartDate = new Date(newStartDate)
        finalStartDate.setHours(
          originalStart.getHours(),
          originalStart.getMinutes(),
          originalStart.getSeconds(),
          originalStart.getMilliseconds()
        )

        const finalEndDate = new Date(newEndDate)
        finalEndDate.setHours(
          originalEnd.getHours(),
          originalEnd.getMinutes(),
          originalEnd.getSeconds(),
          originalEnd.getMilliseconds()
        )

        const schedulingPatterns =
          event.schedulingPatterns?.map((p) => ({
            patternId: p.patternId,
            patternRequestId: p.patternRequestId || null,
          })) || []

        const schedulingEdit = {
          id: event.id,
          fromInstitutionId: event.fromInstitutionId || this.institution.id,
          toInstitutionId: event.toInstitutionId,
          toUnidadeId: event.toUnidadeId,
          alertDate: event.alertDate ? new Date(event.alertDate) : null,
          startDate: finalStartDate,
          endDate: finalEndDate,
          schedulingEventType: event.schedulingEventType,
          details: event.details || '',
          supplierId: event.supplierId || null,
          schedulingPatterns: schedulingPatterns,
        }

        await this.apiService.put(
          'scheduling/edit',
          schedulingEdit,
          timezoneHeader
        )

        this.$toast.success('Agendamento movido! 🔄')
        await this.reloadScheduling()
      } catch (error) {
        this.$toast.error(
          'Erro ao atualizar agendamento: ' +
            (error.response?.data?.message || error.message)
        )
        throw error
      }
    },

    setupCalendarDayHoverListeners() {
      if (!this.$refs.calendar || !this.$refs.calendar.$el) {
        console.log('⚠️ Calendário ainda não está pronto')
        return
      }

      const calendarEl = this.$refs.calendar.$el

      if (this._calendarMouseOverHandler) {
        calendarEl.removeEventListener(
          'mouseover',
          this._calendarMouseOverHandler
        )
      }

      this._calendarMouseOverHandler = (event) => {
        const dayElement =
          event.target.closest('.v-calendar-monthly__day') ||
          event.target.closest('.v-calendar-weekly__day') ||
          event.target.closest('.v-calendar-daily__day')

        if (dayElement) {
          if (!dayElement.querySelector('[data-date]')) return

          if (dayElement === this._lastHoveredDay) return
          this._lastHoveredDay = dayElement

          const dateText = this.extractDateTextFromElement(dayElement)
        }
      }

      calendarEl.addEventListener('mouseover', this._calendarMouseOverHandler)
    },

    hideOutsideDaysCSS() {
      if (
        !this.$refs.calendar ||
        !this.$refs.calendar.$el ||
        this.type !== 'month'
      ) {
        return
      }

      const calendarEl = this.$refs.calendar.$el
      const allDays = calendarEl.querySelectorAll('.v-calendar-monthly__day')

      if (!allDays || allDays.length === 0) {
        return
      }

      // Pega o mês/ano atual da visualização
      if (!this.start || !this.start.month || !this.start.year) {
        return
      }

      const currentMonth = this.start.month
      const currentYear = this.start.year

      allDays.forEach((dayElement) => {
        const button = dayElement.querySelector('button.v-btn')
        if (button) {
          const ariaLabel = button.getAttribute('aria-label')

          if (ariaLabel) {
            const match = ariaLabel.match(
              /(\d{1,2})\s+de\s+([\wç]+)\s+de\s+(\d{4})/
            )

            if (match) {
              const day = parseInt(match[1])
              const monthName = match[2].toLowerCase()
              const year = parseInt(match[3])

              const monthMap = {
                janeiro: 1,
                fevereiro: 2,
                março: 3,
                abril: 4,
                maio: 5,
                junho: 6,
                julho: 7,
                agosto: 8,
                setembro: 9,
                outubro: 10,
                novembro: 11,
                dezembro: 12,
              }

              const month = monthMap[monthName]

              if (month !== currentMonth || year !== currentYear) {
                dayElement.classList.add('outside-month-hidden')
              }
            }
          }
        }
      })
    },

    handleDayHover(event) {
      const dayElement = event.currentTarget
      this.extractDateTextFromElement(dayElement)
    },

    extractDateTextFromElement(element) {
      const labelSpan = element.querySelector('[data-date]')
      if (labelSpan) {
        return labelSpan.getAttribute('data-date')
      }

      const dayButton = element.querySelector('button.v-btn')
      if (dayButton) {
        return (
          dayButton.getAttribute('aria-label') || dayButton.textContent.trim()
        )
      }

      return null
    },
  },
}
</script>

<style scoped>
.calendar-app-bar {
  display: flex;
  align-items: center;
}

.custom-tooltip {
  background-color: white !important;
  color: black !important;
  opacity: 1 !important;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15); /* opcional: sombra suave */
  border: 1px solid #ddd; /* opcional: borda leve */
  padding: 8px;
  font-size: 13px;
  line-height: 1.4;
  font-weight: 400;
  border-radius: 4px;
}

.full-height {
  height: 100%;
}

.card-container {
  position: relative;
  overflow: hidden;
}

.calendar-main-content {
  transition: margin-left 0.3s ease;
}

.drag-nav-zone {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 36px;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(25, 118, 210, 0.2);
  border: 2px dashed rgba(25, 118, 210, 0.45);
  transition: background 0.2s ease, border-color 0.2s ease;
  cursor: ew-resize;
  pointer-events: all;
}
.drag-nav-zone--left {
  left: 0;
  border-left: none;
  border-radius: 0 8px 8px 0;
}
.drag-nav-zone--right {
  right: 0;
  border-right: none;
  border-radius: 8px 0 0 8px;
}
.drag-nav-zone--active {
  background: rgba(25, 118, 210, 0.55);
  border-color: rgba(25, 118, 210, 0.9);
}
.drag-nav-zone__label {
  color: white;
  font-size: 9px;
  font-weight: 600;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  line-height: 1.3;
  padding: 0 2px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  writing-mode: vertical-rl;
}

.drawer-open {
  margin-left: 300px;
}

.calendar-title-bar {
  border-bottom: 1px solid #e0e0e0;
  background: white;
}

.calendar-container {
  height: calc(100% - 70px);
  overflow: auto;
}

.calendar-sidebar {
  border-right: 1px solid #e0e0e0;
  background: white;
  z-index: 5;
}

.mini-calendar {
  width: 100%;
}

.mini-calendar-day {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.mini-calendar-day.has-events {
  background-color: rgba(25, 118, 210, 0.1);
  font-weight: 500;
}

.event-content {
  width: 100%;
  height: 100%;
  padding: 2px 4px;
  display: flex;
  flex-direction: column;
}

.event-title {
  font-weight: 500;
  font-size: 12px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-time {
  font-size: 10px;
  opacity: 0.8;
  line-height: 1.2;
}

/* Custom calendar styling */
::v-deep .v-calendar-daily_head-day-label {
  font-size: 0.875rem;
  font-weight: 500;
}

::v-deep .v-calendar-weekly__week {
  min-height: 150px;
}

::v-deep .v-calendar-monthly__day {
  position: relative;
}

::v-deep .v-calendar-monthly__day-month {
  font-size: 0.875rem;
  font-weight: 500;
}

::v-deep .v-event {
  border-radius: 4px;
  font-size: 12px;
  margin: 1px 0;
  padding: 2px 4px;
  position: relative;
  white-space: nowrap;
  overflow: hidden;
}

::v-deep .v-navigation-drawer__content {
  overflow-y: auto;
}

/* Mini Calendar Styles */
.mini-calendar-container {
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.mini-calendar-header {
  padding: 8px 4px;
}

.mini-calendar-title {
  color: #1976d2;
  cursor: pointer;
  flex: 1;
  text-align: center;
}

.calendar-nav-btn {
  color: #5f6368;
}

.calendar-nav-btn:hover {
  background-color: rgba(95, 99, 104, 0.1);
}

.mini-calendar-grid {
  width: 100%;
}

.mini-calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-color: #f8f9fa;
  border-top: 1px solid #e8eaed;
  border-bottom: 1px solid #e8eaed;
}

.mini-calendar-weekday {
  padding: 8px 4px;
  text-align: center;
  font-size: 11px;
  font-weight: 500;
  color: #5f6368;
}

.mini-calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.mini-calendar-day {
  position: relative;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;
}

.mini-calendar-day:hover {
  background-color: #f1f3f4;
}

.mini-calendar-day.other-month {
  color: #9aa0a6;
}

.mini-calendar-day.today {
  background-color: #1976d2;
  color: white;
  border-radius: 50%;
}

.mini-calendar-day.today:hover {
  background-color: #1565c0;
}

.mini-calendar-day.selected {
  background-color: #e3f2fd;
  border-color: #1976d2;
}

.mini-calendar-day.selected.today {
  background-color: #1976d2;
  color: white;
}

.day-number {
  font-size: 13px;
  font-weight: 400;
  position: relative;
  z-index: 2;
}

.event-indicators {
  position: absolute;
  bottom: 1px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 1px;
  align-items: center;
}

.event-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #1976d2;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.event-dot:hover {
  transform: scale(1.3);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.event-dot-inner {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: inherit;
}

.more-events {
  font-size: 8px;
  color: #5f6368;
  margin-left: 1px;
  cursor: pointer;
  padding: 1px 2px;
  border-radius: 2px;
  transition: all 0.2s ease;
}

.more-events:hover {
  background-color: rgba(95, 99, 104, 0.1);
  color: #1976d2;
}

.mini-calendar-day.has-events .day-number {
  font-weight: 500;
}

/* Patterns Section Styles */
.patterns-section {
  display: flex;
  flex-direction: column;
  height: auto;
  min-height: 300px;
  max-height: calc(100vh - 450px);
}

.patterns-list-container {
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 550px);
  min-height: 200px;
}

.patterns-list {
  padding: 0;
}

.pattern-item {
  border-bottom: 1px solid #f5f5f5;
  transition: background-color 0.2s ease;
}

.pattern-item:hover {
  background-color: #f8f9fa;
}

.pattern-selected {
  background-color: #e3f2fd !important;
  border-left: 4px solid #1976d2;
}

.pattern-selected:hover {
  background-color: #e3f2fd !important;
}

.pattern-dragging {
  opacity: 0.5;
  transform: scale(0.95);
  transition: all 0.2s ease;
}

.drag-handle {
  color: #9e9e9e;
  cursor: grab;
}

.drag-handle:hover {
  color: #1976d2;
}

.pattern-item[draggable='true'] {
  cursor: grab;
}

.pattern-item[draggable='true']:active {
  cursor: grabbing;
}

/* Custom scrollbar for patterns list */
.patterns-list-container::-webkit-scrollbar {
  width: 6px;
}

.patterns-list-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.patterns-list-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.patterns-list-container::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .mini-calendar-day {
    height: 28px;
  }

  .day-number {
    font-size: 12px;
  }

  .patterns-section {
    max-height: calc(100vh - 400px);
    min-height: 250px;
  }

  .patterns-list-container {
    max-height: calc(100vh - 500px);
    min-height: 150px;
  }
}

/* Zoom adjustments */
@media (min-resolution: 120dpi) {
  .patterns-section {
    max-height: calc(100vh - 400px);
  }

  .patterns-list-container {
    max-height: calc(100vh - 500px);
  }
}

/* Drag and Drop Calendar Styles */
.calendar-drop-zone {
  position: relative;
  transition: all 0.3s ease;
  height: 100%;
}

.calendar-drop-zone.drag-over {
  background-color: rgba(25, 118, 210, 0.05);
}

.calendar-drop-zone.drag-over::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px dashed #1976d2;
  border-radius: 4px;
  pointer-events: none;
  z-index: 1;
}

/* Highlight permanente em cada dia do calendário */
::v-deep .v-calendar-monthly__day,
::v-deep .v-calendar-daily__day,
::v-deep .v-calendar-weekly__day {
  position: relative;
  border: 1px solid rgba(25, 118, 210, 0.15) !important;
  background: linear-gradient(
    135deg,
    rgba(25, 118, 210, 0.03) 0%,
    rgba(255, 255, 255, 0.8) 100%
  ) !important;
  transition: all 0.2s ease;
}

/* Highlight when dragging over a valid drop zone */
::v-deep .v-calendar-monthly__day.drag-over,
::v-deep .v-calendar-daily__day.drag-over,
::v-deep .v-calendar-weekly__day.drag-over {
  background-color: rgba(25, 118, 210, 0.2) !important;
  border: 2px solid #1976d2 !important;
}

/* Destaque visual para o dia alvo durante o drag */
::v-deep .v-calendar-monthly__day.day-drag-target {
  background: linear-gradient(
    135deg,
    rgba(25, 118, 210, 0.25) 0%,
    rgba(25, 118, 210, 0.15) 100%
  ) !important;
  border: 3px solid #1976d2 !important;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(25, 118, 210, 0.4);
  transform: scale(1.03);
  transition: all 0.2s ease;
  z-index: 10;
  position: relative;
}

::v-deep .v-calendar-monthly__day.day-drag-target button {
  font-weight: 700 !important;
  color: #1976d2 !important;
  font-size: 1.15em !important;
}

/* Evento sendo arrastado */
.v-event-draggable.event-dragging {
  opacity: 0.4 !important;
  transform: scale(0.98);
  filter: grayscale(0.3);
  transition: all 0.2s ease;
}

/* Visual feedback for draggable events */
::v-deep .v-event {
  transition: opacity 0.2s ease, transform 0.2s ease;
  user-select: none;
  cursor: grab;
}

::v-deep .v-event:hover {
  transform: scale(1.02);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

::v-deep .v-event:active {
  cursor: grabbing;
}

::v-deep .v-event[draggable='true'] {
  cursor: move !important;
}

::v-deep .v-event.dragging {
  opacity: 0.5;
  cursor: grabbing !important;
}

/* Event Drag and Drop Styles */
.v-event-draggable {
  padding-left: 6px;
  width: 100%;
  height: 100%;
}

.v-event-draggable[draggable='true'] {
  cursor: move !important;
}

.v-event-draggable[draggable='true']:active {
  cursor: grabbing !important;
  opacity: 0.8;
}

.v-event-timed {
  user-select: none;
  -webkit-user-select: none;
}

.v-event-drag-bottom {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 4px;
  height: 4px;
  cursor: ns-resize;
}

.v-event-drag-bottom::after {
  display: none;
  position: absolute;
  left: 50%;
  height: 4px;
  border-top: 1px solid white;
  border-bottom: 1px solid white;
  width: 16px;
  margin-left: -8px;
  opacity: 0.8;
  content: '';
}

.v-event-drag-bottom:hover::after {
  display: block;
}

/* Calendar time slot hover effect */
::v-deep .v-calendar-timed__time-content:hover {
  background-color: rgba(25, 118, 210, 0.05);
}

/* Prevent text selection during dragging */
.v-calendar-main {
  user-select: none;
}
</style>
